import Tippy from '@tippyjs/react';
import React, { useCallback } from 'react';

import { LimitPeriodDetailList } from '@/bits/limit-period-detail-list/component';
import {
  Bar,
  ConditionalWrap,
  ControlledModal,
  InlineIconButton,
} from '@/components';
import {
  CloseIcon,
  EditIcon,
  InformationIcon,
  TrashIcon,
} from '@/components/icons';
import { useTranslate } from '@/contexts';
import {
  PlayerCancelGlobalLossLimitForm,
  PlayerGlobalLossLimitsForm,
  PlayerRemoveGlobalLossLimitForm,
} from '@/forms';
import { PeriodLimitFragment } from '@/fragments/__generated__/Limits';
import { LimitType, LimitTypeEnum } from '@/globalTypes';
import { Nullable } from '@/types';
import formatDate from '@/utils/formatter/formatDate';
import formatMoney from '@/utils/formatter/formatMoney';
import { PlayerGlobalLossLimitsBoxQuery } from './__generated__/component';

type LimitPeriod =
  | NonNullable<
      PlayerGlobalLossLimitsBoxQuery['player']['walletWithGlobalLimits']
    >['lossLimit']['weekLimit']
  | NonNullable<
      PlayerGlobalLossLimitsBoxQuery['player']['walletWithGlobalLimits']
    >['lossLimit']['monthLimit'];

const getLimitBarValue = (limit: Nullable<LimitPeriod>) => {
  const value = limit?.value ?? 0;
  const available = limit?.available ?? 0;
  return limit ? 0 : (value - available) / value;
};

const PendingPeriod = ({
  limit,
  currency,
  playerId,
  limitPeriodType,
}: {
  limit: Nullable<PeriodLimitFragment>;
  currency: Nullable<string>;
  playerId: Nullable<string>;
  limitPeriodType: LimitTypeEnum;
}) => {
  if (!limit?.pendingPeriodLimit) {
    return null;
  }

  return (
    <div className="italic dark:text-white">
      `Limit will be updated to{' '}
      {formatMoney(limit.pendingPeriodLimit.value, currency)} on{' '}
      {formatDate(limit.pendingPeriodLimit.validFrom)}`
      {playerId ? (
        <ControlledModal
          content={
            <PlayerCancelGlobalLossLimitForm
              playerId={playerId}
              limitPeriodType={limitPeriodType}
            />
          }
        >
          <InlineIconButton>
            <CloseIcon />
          </InlineIconButton>
        </ControlledModal>
      ) : null}
    </div>
  );
};

const PendingLimitRemovalMessage = ({
  limit,
}: {
  limit: Nullable<LimitPeriod>;
}) => {
  if (!limit?.pendingPeriodLimitRemoval) {
    return null;
  }

  return (
    <div className="italic dark:text-white">
      `Limit will be removed on{' '}
      {formatDate(limit.pendingPeriodLimitRemoval.validFrom)}`
    </div>
  );
};

type GlobalLossLimitBarProps = {
  playerId?: string;
  fetching: boolean;
  limit: Nullable<LimitPeriod>;
  currency: Nullable<string>;
  label: Nullable<Queries.SanityLocaleStringFragment>;
  limitPeriodType: LimitTypeEnum;
};

export const GlobalLossLimitBar = ({
  fetching,
  limit,
  currency,
  label,
  playerId,
  limitPeriodType,
}: GlobalLossLimitBarProps) => {
  const { t } = useTranslate();

  const currencyValueFormatter = useCallback(
    (value: Nullable<number>) => formatMoney(value, currency),
    [currency],
  );

  return (
    <div>
      <div className="flex">
        <ConditionalWrap
          condition={!!limit}
          wrap={(children) => (
            <Tippy
              content={`Period ending: ${formatDate(
                limit?.currentPeriodEndTime,
              )}`}
            >
              <div className="w-full">{children}</div>
            </Tippy>
          )}
        >
          <Bar loading={fetching} value={getLimitBarValue(limit)}>
            {t(label, {
              value: formatMoney((limit?.value ?? 0) - (limit?.available ?? 0)),
              of: formatMoney(limit?.value, currency),
            })}
          </Bar>
        </ConditionalWrap>
        <ControlledModal
          content={
            playerId && limit ? (
              <LimitPeriodDetailList
                limit={limit}
                limitTypeTitle={LimitType.Loss}
                limitPeriodType={limitPeriodType}
                formatValue={currencyValueFormatter}
              />
            ) : null
          }
        >
          <InlineIconButton disabled={!limit}>
            <InformationIcon />
          </InlineIconButton>
        </ControlledModal>
        <ControlledModal
          content={
            playerId ? (
              <PlayerGlobalLossLimitsForm
                playerId={playerId}
                limitPeriodType={limitPeriodType}
                currency={currency}
                limit={limit}
              />
            ) : null
          }
        >
          <InlineIconButton>
            <EditIcon />
          </InlineIconButton>
        </ControlledModal>
        <ControlledModal
          content={
            playerId ? (
              <PlayerRemoveGlobalLossLimitForm
                playerId={playerId}
                limitPeriodType={limitPeriodType}
              />
            ) : null
          }
        >
          <InlineIconButton>
            <TrashIcon />
          </InlineIconButton>
        </ControlledModal>
      </div>
      <PendingPeriod
        currency={currency}
        limit={limit}
        limitPeriodType={limitPeriodType}
        playerId={playerId}
      />
      <PendingLimitRemovalMessage limit={limit} />
    </div>
  );
};
